import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Camera, CheckCircle2, XCircle, Loader2, AlertTriangle, Info, Sun, Square, Eye } from 'lucide-react';
import { verifications } from '../lib/api';
import LivenessCheck, { LivenessResult } from '../components/LivenessCheck';

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:3001');

type HandoffStatus = 'loading' | 'ready' | 'liveness' | 'document-capture' | 'uploading' | 'completed' | 'error' | 'expired';

import { compressImage } from '../lib/imageCompression';

export default function Handoff() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const secret = searchParams.get('secret');

  const [status, setStatus] = useState<HandoffStatus>('loading');
  const [sessionType, setSessionType] = useState<'liveness' | 'document'>('liveness');
  const [docFront, setDocFront] = useState<Blob | null>(null);
  const [docBack, setDocBack] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [livenessResult, setLivenessResult] = useState<LivenessResult | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isAssisted, setIsAssisted] = useState(false);

  // Validate session on load
  useEffect(() => {
    const validateSession = async () => {
      if (!sessionId || !secret) {
        setError('Invalid handoff link. Please scan the QR code again.');
        setStatus('error');
        return;
      }

      try {
        console.log('Validating handoff session:', sessionId);
        const sessionStatus = await verifications.getHandoffStatus(sessionId);
        console.log('Session status:', sessionStatus);

        if (sessionStatus.status === 'expired') {
          setStatus('expired');
          return;
        }

        if (sessionStatus.status === 'completed') {
          setStatus('completed');
          return;
        }

        console.log('Setting verificationId:', sessionStatus.verificationId);
        setVerificationId(sessionStatus.verificationId);
        setSessionType(sessionStatus.sessionType || 'liveness');
        setIsAssisted(!!sessionStatus.isAssisted);
        
        if (sessionStatus.sessionType === 'document') {
           setStatus('document-capture');
           setSessionType('document');
        } else {
           setStatus('ready'); // Ready for liveness
           setSessionType('liveness');
        }

        // Mark session as opened
        await fetch(`${API_URL}/api/verifications/handoff/${sessionId}/opened`, {
          method: 'POST',
          credentials: 'include',
        });
      } catch (err) {
        console.error('Session validation failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to validate session');
        setStatus('error');
      }
    };

    validateSession();
  }, [sessionId, secret]);

  // Handle liveness check completion
  const handleLivenessComplete = async (result: LivenessResult) => {
    setLivenessResult(result);
    console.log('Liveness result:', result);

    if (result.passed && result.selfieBlob && verificationId) {
      try {
        setStatus('uploading');
        console.log('Uploading selfie for verification:', verificationId);

        // Upload selfie to server
        await verifications.uploadSelfie(verificationId, result.selfieBlob, undefined, sessionId!);
        console.log('Selfie uploaded successfully');

        // Complete handoff
        console.log('Completing handoff for session:', sessionId);
        const completeResponse = await fetch(`${API_URL}/api/verifications/handoff/${sessionId}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ secret }),
        });

        if (!completeResponse.ok) {
          const errorData = await completeResponse.json().catch(() => ({}));
          console.error('Handoff complete failed:', completeResponse.status, errorData);
          throw new Error(errorData.error || 'Failed to complete handoff');
        }

        console.log('Handoff completed successfully');
        setStatus('completed');
      } catch (err) {
        console.error('Upload/complete error:', err);
        setError(err instanceof Error ? err.message : 'Failed to complete verification');
        setStatus('ready');
      }
    } else {
      console.log('Liveness check failed:', result.failureReasons);
      setError(`Liveness check failed: ${result.failureReasons.join(', ') || 'Please try again with better lighting.'}`);
      setStatus('ready');
    }
  };

  // Handle document submission
  const handleDocumentSubmit = async () => {
    if (!docFront) {
      setError('Please capture the front of your ID first.');
      return;
    }
    if (!verificationId || !sessionId) {
      setError('Session error — please close this page and scan the QR code again.');
      return;
    }

    try {
      setStatus('uploading');
      
      // Upload documents
      await verifications.uploadDocument(
        verificationId,
        docFront,
        docBack || undefined,
        undefined, // No guest token
        sessionId // Pass handoff session ID
      );

      // Complete handoff
      const completeResponse = await fetch(`${API_URL}/api/verifications/handoff/${sessionId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ secret }),
      });

      if (!completeResponse.ok) {
        throw new Error('Failed to complete handoff');
      }

      setStatus('completed');
    } catch (err) {
      console.error('Document upload error:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload documents');
      // Return to capture state on error
      setStatus('document-capture');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-xl p-6 shadow-xl">
        {/* Header */}
        <div className="text-center mb-6">
          <img src="/Agos_logo.png" alt="Agos" className="w-12 h-12 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-white">Liveness Check</h1>
          <p className="text-gray-400 text-sm">Complete your identity verification</p>
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div className="text-center py-8">
            <Loader2 className="w-10 h-10 text-blue-400 mx-auto animate-spin mb-3" />
            <p className="text-gray-400">Validating session...</p>
          </div>
        )}

        {/* Expired */}
        {status === 'expired' && (
          <div className="text-center py-8">
            <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-white mb-2">Session Expired</h2>
            <p className="text-gray-400 text-sm">
              This handoff link has expired. Please generate a new QR code from your desktop.
            </p>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="text-center py-8">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-white mb-2">Error</h2>
            <p className="text-red-400 text-sm mb-4">{error}</p>
            <button
              type="button"
              onClick={() => { setError(null); setStatus('ready'); }}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Ready - Start Liveness Check */}
        {status === 'ready' && (
          <div className="text-center py-8">
            <Camera className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <p className="text-gray-300 mb-4">
              Complete a liveness check to verify your identity.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              You'll be asked to blink, turn your head, or smile to prove you're a real person.
            </p>
            {error && (
              <p className="text-red-400 text-sm mb-4">{error}</p>
            )}
            <button
              type="button"
              onClick={() => setStatus('liveness')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Start Liveness Check
            </button>
          </div>
        )}

        {/* Liveness Check Component */}
        {status === 'liveness' && (
          <LivenessCheck
            onComplete={handleLivenessComplete}
            onCancel={() => setStatus('ready')}
            language="en"
            isAssisted={isAssisted}
          />
        )}

        {/* Document Capture Component */}
        {status === 'document-capture' && (
          <div className="text-center">
             <h2 className="text-xl font-bold text-white mb-2">Capture ID Document</h2>
             <p className="text-gray-400 text-sm mb-4">
               Take a clear photo of your government ID.
             </p>

             {/* Guidance Overlay */}
             <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-4 mb-5">
               <div className="flex items-center gap-2 mb-3">
                 <Info className="w-4 h-4 text-blue-400 flex-shrink-0" />
                 <p className="text-blue-300 text-sm font-medium text-left">Photo Tips for Best Results</p>
               </div>
               <div className="grid grid-cols-2 gap-3 text-left">
                 <div className="flex items-start gap-2">
                   <Sun className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                   <span className="text-gray-300 text-xs">Use good, even lighting</span>
                 </div>
                 <div className="flex items-start gap-2">
                   <Square className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                   <span className="text-gray-300 text-xs">All 4 corners visible</span>
                 </div>
                 <div className="flex items-start gap-2">
                   <Eye className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                   <span className="text-gray-300 text-xs">Text must be readable</span>
                 </div>
                 <div className="flex items-start gap-2">
                   <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                   <span className="text-gray-300 text-xs">Avoid glare & shadows</span>
                 </div>
               </div>
             </div>

             {/* Error display */}
             {error && (
               <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3 mb-4">
                 <p className="text-red-400 text-sm">{error}</p>
               </div>
             )}

             <div className="space-y-5">
               {/* Front ID */}
               <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                 <p className="text-white font-medium mb-3">Front of ID <span className="text-red-400 text-xs">*Required</span></p>
                 {docFront ? (
                   <div className="relative">
                     <img src={URL.createObjectURL(docFront)} alt="Front ID" className="w-full rounded-lg mb-3" />
                     <button
                       onClick={() => { setDocFront(null); setError(null); }}
                       className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full shadow-lg"
                     >
                       <XCircle className="w-5 h-5" />
                     </button>
                   </div>
                 ) : (
                   <label className="block w-full p-6 border-2 border-dashed border-gray-500 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-gray-600/50 transition-colors">
                     <Camera className="w-10 h-10 text-blue-400 mx-auto mb-2" />
                     <span className="text-sm text-gray-300 font-medium">{isProcessing ? 'Processing Image...' : 'Tap to Capture Front'}</span>
                     <span className="block text-xs text-gray-500 mt-1">Back camera will open automatically</span>
                     <input
                       type="file"
                       accept="image/*"
                       capture="environment"
                       className="hidden"
                       disabled={isProcessing}
                       onChange={async (e) => {
                         if (e.target.files?.[0]) {
                           try {
                             setIsProcessing(true);
                             const compressed = await compressImage(e.target.files[0]);
                             setDocFront(compressed); 
                             setError(null);
                           } catch (err) {
                             setError('Failed to process image. Please try again.');
                           } finally {
                             setIsProcessing(false);
                           }
                         }
                       }}
                     />
                   </label>
                 )}
               </div>

               {/* Back ID */}
               <div className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                 <p className="text-white font-medium mb-3">Back of ID <span className="text-gray-400 text-xs font-normal">(Optional)</span></p>
                 {docBack ? (
                   <div className="relative">
                     <img src={URL.createObjectURL(docBack)} alt="Back ID" className="w-full rounded-lg mb-3" />
                     <button
                       onClick={() => setDocBack(null)}
                       className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full shadow-lg"
                     >
                       <XCircle className="w-5 h-5" />
                     </button>
                   </div>
                 ) : (
                   <label className="block w-full p-6 border-2 border-dashed border-gray-500 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-gray-600/50 transition-colors">
                     <Camera className="w-10 h-10 text-blue-400 mx-auto mb-2" />
                     <span className="text-sm text-gray-300 font-medium">{isProcessing ? 'Processing Image...' : 'Tap to Capture Back'}</span>
                     <span className="block text-xs text-gray-500 mt-1">For IDs with info on both sides</span>
                     <input
                       type="file"
                       accept="image/*"
                       capture="environment"
                       className="hidden"
                       disabled={isProcessing}
                       onChange={async (e) => {
                         if (e.target.files?.[0]) {
                            try {
                              setIsProcessing(true);
                              const compressed = await compressImage(e.target.files[0]);
                              setDocBack(compressed);
                            } catch (err) {
                              setError('Failed to process image. Please try again.');
                            } finally {
                              setIsProcessing(false);
                            }
                         }
                       }}
                     />
                   </label>
                 )}
               </div>

               <button
                 onClick={handleDocumentSubmit}
                 disabled={!docFront || isProcessing}
                 className={`w-full py-3 rounded-lg font-bold text-white transition-colors ${
                   docFront && !isProcessing ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                 }`}
               >
                 {isProcessing ? 'Processing...' : 'Submit Documents'}
               </button>
             </div>
          </div>
        )}

        {/* Uploading */}
        {status === 'uploading' && (
          <div className="text-center py-8">
            <Loader2 className="w-10 h-10 text-blue-400 mx-auto animate-spin mb-3" />
            <p className="text-gray-400">Uploading documents...</p>
          </div>
        )}

        {/* Completed */}
        {status === 'completed' && (

          <div className="text-center py-8">
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">
              {sessionType === 'liveness' ? 'Liveness Verified!' : 'Documents Uploaded!'}
            </h2>
            <p className="text-gray-400 text-sm mb-4">
              {sessionType === 'liveness'
                ? 'Your liveness check has been successfully verified. You can now return to your desktop.'
                : 'Your documents have been securely received. You can now close this page and return to your desktop to continue.'}
            </p>
            {sessionType === 'document' && (
              <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 text-sm">
                  ✓ Front ID received<br />
                  {docBack && '✓ Back ID received\n'}
                  ✓ Securely synced
                </p>
              </div>
            )}
            {sessionType === 'liveness' && (
              <div className="bg-green-900/30 border border-green-500/30 rounded-lg p-4">
                <p className="text-green-400 text-sm">
                  ✓ Selfie captured<br />
                  ✓ Face match verified<br />
                  ✓ Securely synced
                </p>
               </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

